// @flow

import React, { Component } from 'react';
import marked from 'marked';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/theme/dracula.css';
import 'codemirror/addon/scroll/simplescrollbars.css';
import styled from 'styled-components';
import Toolbar from '../ToolBar/Toolbar';
import palette from '../ToolBar/palette';
import './CodeEditor.scss';

let CodeMirror = null;

if (
  process.env.APP_ENV !== 'server' &&
  typeof window !== 'undefined' &&
  typeof window.navigator !== 'undefined'
) {
  CodeMirror = require('codemirror');
  require('codemirror/addon/scroll/simplescrollbars');
  require('codemirror/addon/display/placeholder');
  require('codemirror/mode/markdown/markdown');
}

function checkiOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

const MarkdownEditorBlock = styled.div`
  height: 100%;
  overflow-y: auto;
  position: relative;
  &::-webkit-scrollbar {
    border-radius: 3px;
    width: 6px;
    &:hover {
      width: 16px;
    }
    background: ${palette.gray1};
  }

  &::-webkit-scrollbar-thumb {
    z-index: 100;
    background: ${palette.gray9};
    /* -webkit-box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.75); */
  }

  & > .wrapper {
    padding-top: 3rem;
    padding-bottom: 10rem;
  }
  .CodeMirror {
    height: auto;
    font-size: 1.125rem;
    line-height: 1.5;
    color: ${palette.gray8};
    font-family: 'Fira Mono', monospace;
    /* font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', */
      monospace;
    .cm-header {
      line-height: 2;
      color: ${palette.gray9};
    }
    .cm-header-1 {
      font-size: 2.5rem;
    }
    .cm-header-2 {
      font-size: 2rem;
    }
    .cm-header-3 {
      font-size: 1.5rem;
    }
    .cm-header-4,
    .cm-header-5,
    .cm-header-6 {
      font-size: 1.3125rem;
    }
    .cm-strong,
    .cm-em {
      color: ${palette.gray9};
    }
    .CodeMirror-placeholder {
      color: ${palette.gray5};
      font-style: italic;
    }
  }
`;

const checker = {
  youtube: text => {
    const regex = /^<iframe.*src="https:\/\/www.youtube.com\/embed\/(.*?)".*<\/iframe>$/;
    const result = regex.exec(text);
    if (!result) return null;
    return result[1];
  },
  twitter: (text: string) => {
    if (!/^<blockquote class="twitter-tweet/.test(text)) return null;
    const regex = /href="(.*?)"/g;
    const links = [];
    let match = regex.exec(text);
    while (match) {
      links.push(match[1]);
      match = regex.exec(text);
    }
    const code = /twitter.com\/(.*?)\?/.exec(links[links.length - 1])[1];
    return code;
  },
  codesandbox: text => {
    const regex = /^<iframe src="https:\/\/codesandbox.io\/embed\/(.*?)".*<\/iframe>$/;
    const result = regex.exec(text);
    if (!result) return null;
    return result[1];
  },
};

const checkEmbed = text => {
  const keys = Object.keys(checker);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const fn = checker[keys[i]];
    const code = fn(text);
    if (code) {
      return `!${key}[${code}]`;
    }
  }
  return null;
};

type Props = {
  body: string,
  onEditBody(value: string): any,
  onDragEnter(e: any): void,
  onDragLeave(e: any): void,
  onClearInsertText(): void,
  onUploadClick(): void,
  insertText: ?string,
};

type State = {
  cursor: any,
  iOS: boolean,
  shadow: boolean,
  addLink: {
    top: number,
    left: number,
    visible: boolean,
    stickToRight: boolean,
  },
  clientWidth: number,
  askChangeEditor: boolean,
};

class CodeEditor extends Component<Props, State> {
  codeMirror: any;
  editor: any;
  prevScrollTop: number;
  scrollBefore: number; // changes only when active scroll line changes
  prevOffsetTop: number;
  prevPreviewScrollTop: number;
  state = {
    cursor: null,
    iOS: false,
    shadow: false,
    addLink: {
      top: 0,
      left: 0,
      visible: false,
      stickToRight: false,
    },
    clientWidth: 0,
    askChangeEditor: false,
  };

  block = React.createRef();
  toolbarElement = React.createRef();
  toolbarTop = 0;

  insertText = () => {
    const { insertText, onClearInsertText } = this.props;
    const editor = this.codeMirror;
    const selection = editor.getSelection();

    if (selection.length > 0) {
      editor.replaceSelection(insertText);
    } else {
      const doc = editor.getDoc();
      const cursor = doc.getCursor();

      const pos = {
        line: cursor.line,
        ch: cursor.ch,
      };

      doc.replaceRange(insertText, pos);
    }
    onClearInsertText();
  };

  onScroll = (e: any) => {
    // retrieve current scroll info and line number
    const scrollInfo = e.getScrollInfo();
    const lineNumber = e.lineAtHeight(scrollInfo.top, 'local');
    const preview = document.getElementById('preview');

    if (!preview) return;
    const down = this.prevScrollTop < scrollInfo.top;
    this.prevScrollTop = scrollInfo.top;

    // content from line 0 -> lineNumber
    const range = e.getRange(
      {
        line: 0,
        ch: null,
      },
      {
        line: lineNumber,
        ch: null,
      },
    );
    const { top, clientHeight, height } = scrollInfo;

    // scroll to bottom
    if (height - clientHeight - top < 50) {
      (preview: any).scroll({
        behavior: 'smooth',
        top: preview.scrollHeight,
      });
      return;
    }

    // count ```
    const count = (range.match(/\n```/g) || []).length;
    const shouldCloseCodeblock = count % 2 === 1 ? '\n```' : '';
    // convert to markdown
    const markdown = `<h1></h1><div>${marked(`${range}${shouldCloseCodeblock}`)}</div>`;
    // create DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(markdown, 'text/html');
    if (!doc.body) return;
    // count lines
    const totalLines = doc.body.querySelectorAll(
      'p, h1, h2, h3, h4, h5, h6, li, pre, blockquote, hr, table',
    );
    const markdownRender = document.getElementById('markdown-render');
    if (!markdownRender || !preview) return;
    // select all lines
    const elements = markdownRender.querySelectorAll(
      'p, h1, h2, h3, h4, h5, h6, li, pre, blockquote, hr, table',
    );
    if (!elements) return;
    // retrieve scrollTop of rendered current line
    const index = totalLines.length > elements.length ? elements.length : totalLines.length;
    if (!elements[index - 1]) return;
    const { offsetTop } = elements[index - 1];
    // if pointing to same element, calculate diff and apply
    if (this.prevOffsetTop !== offsetTop) {
      this.scrollBefore = scrollInfo.top;
    }
    const diff = this.prevOffsetTop === offsetTop ? scrollInfo.top - this.scrollBefore : 0;
    this.prevOffsetTop = offsetTop;
    const previewScrollTop = offsetTop + diff;
    if (previewScrollTop > this.prevPreviewScrollTop && !down) return;
    this.prevPreviewScrollTop = previewScrollTop;
    // actually scroll
    (preview: any).scroll({
      behavior: 'smooth',
      top: previewScrollTop,
    });
  };

  initialize = () => {
    if (!CodeMirror) return;
    if (checkiOS()) {
      this.setState({
        iOS: true,
      });
      return;
    }
    const { onDragEnter, onDragLeave } = this.props;
    this.codeMirror = CodeMirror(this.editor, {
      mode: 'markdown',
      theme: 'material',
      lineNumbers: false, // 좌측에 라인넘버 띄우기
      lineWrapping: true, // 내용이 너무 길면 다음 줄에 등록
      scrollbarStyle: 'overlay',
      placeholder: '내용을 입력하세요.',
    });
    window.codeMirror = this.codeMirror; // for debugging use
    this.codeMirror.on('focus', () => {
      if (this.props.body === '' && window.outerWidth < 768) {
        this.codeMirror.options.placeholder = '';
        this.codeMirror.setValue(' ');
        this.codeMirror.setValue('');
      }
    });
    this.codeMirror.on('blur', () => {
      if (this.props.body === '' && window.outerWidth < 768) {
        this.codeMirror.options.placeholder = '당신의 설명를 적어보세요.';
        this.codeMirror.setValue(' ');
        this.codeMirror.setValue('');
      }
    });
    this.codeMirror.on('change', this.onChange);
    this.codeMirror.on('scroll', this.onScroll);
    this.codeMirror.on('dragenter', (event, e) => onDragEnter(e));
    this.codeMirror.on('dragleave', (event, e) => onDragLeave(e));
    this.codeMirror.on('paste', (editor, e) => {
      const { items } = e.clipboardData || e.originalEvent.clipboardData;
      if (e.clipboardData) {
        const text = e.clipboardData.getData('Text');
        const check = checkEmbed(text);
        if (!check) return;
        const selection = editor.getSelection();
        if (selection.length > 0) {
          editor.replaceSelection(check);
        } else {
          const doc = editor.getDoc();
          const cursor = doc.getCursor();
          const pos = {
            line: cursor.line,
            ch: cursor.ch,
          };
          doc.replaceRange(check, pos);
        }
        e.preventDefault();
        return;
      }
      if (items.length !== 2) return;
      if (items[1].kind !== 'file') return;
      e.preventDefault();
    });
    if (this.props.body) {
      this.codeMirror.setValue(this.props.body);
    }
    // TODO: load data (for updating)
  };

  onChange = (doc: any) => {
    const cursor = doc.getCursor();
    this.setState({
      cursor,
    });
    const { onEditBody, body } = this.props;
    if (body !== doc.getValue()) {
      onEditBody(doc.getValue());
    }
    // if editing the last line
    const { line } = cursor;
    const last = doc.lastLine();
    if (last - line < 5) {
      const preview = document.getElementById('preview');
      if (!preview) return;
      preview.scrollTop = preview.scrollHeight;
    }
  };

  onTextareaChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.props.onEditBody(e.target.value);
  };

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { codeMirror } = this;
    const { cursor } = this.state;
    const { body } = this.props;

    if (!codeMirror) return;
    // diff cursor
    if (prevState.cursor !== cursor) {
      codeMirror.setCursor(cursor);
      return;
    }
    if (prevProps.body !== body && body !== this.codeMirror.getValue()) {
      codeMirror.setValue(body);
      if (!cursor) return;
      codeMirror.setCursor(cursor);
    }
    if (!prevProps.insertText && this.props.insertText) {
      this.insertText();
    }
  }

  handleOpenAddLink = () => {
    if (!this.codeMirror) return;
    const doc = this.codeMirror.getDoc();
    const cursor = doc.getCursor();
    const cursorPos = this.codeMirror.cursorCoords(cursor);
    if (!this.block.current) return;
    const stickToRight = cursorPos.left > this.block.current.clientWidth - 341;

    this.setState({
      addLink: {
        visible: true,
        top:
          this.block.current.scrollTop +
          cursorPos.top +
          this.codeMirror.defaultTextHeight() / 2 +
          1,
        left: cursorPos.left,
        stickToRight,
      },
    });
  };

  handleToolbarClick = (mode: string) => {
    const codemirror = this.codeMirror;

    if (!codemirror) return;
    const doc = codemirror.getDoc();

    const cursor = doc.getCursor();
    const selection = {
      start: doc.getCursor('start'),
      end: doc.getCursor('end'),
    };

    const line = doc.getLine(cursor.line);

    const selectWholeLine = () => {
      doc.setSelection(
        {
          line: cursor.line,
          ch: 0,
        },
        {
          line: cursor.line,
          ch: line.length,
        },
      );
    };

    const removeHeading = (text: string) => {
      return text.replace(/#{1,6} /, '');
    };

    const handlers: {
      [key: string]: Function,
    } = {
      ...[1, 2, 3, 4] // creates handlers for heading1, heading2, heading3, heading4
        .map(number => () => {
          // create handler function
          const characters = '#'.repeat(number);
          const plain = removeHeading(line);
          selectWholeLine();
          doc.replaceSelection(`${characters} ${plain}`);
        })
        .reduce((headingHandlers, handler, index) => {
          // reduce into handlers object
          return Object.assign(headingHandlers, {
            [`heading${index + 1}`]: handler,
          });
        }, {}),
      bold: () => {
        const selected = doc.getSelection();
        if (selected === '텍스트') {
          const isBold = /\*\*(.*)\*\*/.test(
            doc.getRange(
              { line: selection.start.line, ch: selection.start.ch - 2 },
              { line: selection.end.line, ch: selection.end.ch + 2 },
            ),
          );

          if (isBold) {
            doc.setSelection(
              { line: selection.start.line, ch: selection.start.ch - 2 },
              { line: selection.end.line, ch: selection.end.ch + 2 },
            );
            doc.replaceSelection('텍스트');
            doc.setSelection(
              { line: selection.start.line, ch: selection.start.ch - 2 },
              { line: selection.end.line, ch: selection.end.ch - 2 },
            );
            return;
          }
        }
        if (/\*\*(.*)\*\*/.test(selected)) {
          // matches **string**
          doc.replaceSelection(selected.replace(/\*\*/g, ''));
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 4 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`**${selected}**`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 4 },
          );
          return;
        }
        doc.replaceSelection('**텍스트**');
        doc.setSelection(
          {
            line: cursor.line,
            ch: cursor.ch + 2,
          },
          {
            line: cursor.line,
            ch: cursor.ch + 5,
          },
        );
      },
      italic: () => {
        let selected = doc.getSelection();

        if (selected.length === 0) {
          doc.replaceSelection('_텍스트_');
          doc.setSelection(
            {
              line: cursor.line,
              ch: cursor.ch + 1,
            },
            {
              line: cursor.line,
              ch: cursor.ch + 4,
            },
          );
          return;
        }

        if (selected === '텍스트') {
          const selectLeftAndRight = doc.getRange(
            {
              line: selection.start.line,
              ch: selection.start.ch - 1,
            },
            {
              line: selection.end.line,
              ch: selection.end.ch + 1,
            },
          );
          if (/_(.*)_/.test(selectLeftAndRight)) {
            selected = selectLeftAndRight;
            doc.setSelection(
              {
                line: selection.start.line,
                ch: selection.start.ch - 1,
              },
              {
                line: selection.end.line,
                ch: selection.end.ch + 1,
              },
            );
            selection.start = {
              line: selection.start.line,
              ch: selection.start.ch - 1,
            };
            selection.end = {
              line: selection.end.line,
              ch: selection.end.ch + 1,
            };
          }
        }

        if (/_(.*)_/.test(selected)) {
          const plain = selected
            .replace(/^_/, '') // remove starting _
            .replace(/_$/, ''); // remove ending _
          doc.replaceSelection(plain);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 2 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`_${selected}_`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 2 },
          );
        }
      },
      strike: () => {
        let selected = doc.getSelection();

        if (selected.length === 0) {
          doc.replaceSelection('~~텍스트~~');
          doc.setSelection(
            {
              line: cursor.line,
              ch: cursor.ch + 2,
            },
            {
              line: cursor.line,
              ch: cursor.ch + 5,
            },
          );
          return;
        }

        if (selected === '텍스트') {
          const selectLeftAndRight = doc.getRange(
            {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            },
            {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            },
          );
          if (/~~(.*)~~/.test(selectLeftAndRight)) {
            selected = selectLeftAndRight;
            doc.setSelection(
              {
                line: selection.start.line,
                ch: selection.start.ch - 2,
              },
              {
                line: selection.end.line,
                ch: selection.end.ch + 2,
              },
            );
            selection.start = {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            };
            selection.end = {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            };
          }
        }

        if (/~~(.*)~~/.test(selected)) {
          const plain = selected
            .replace(/^~~/, '') // remove starting ~~
            .replace(/~~$/, ''); // remove ending ~~
          doc.replaceSelection(plain);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 4 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`~~${selected}~~`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 4 },
          );
        }
      },
      blockquote: () => {
        const matches = /^> /.test(line);
        doc.setSelection({ line: cursor.line, ch: 0 }, { line: cursor.line, ch: line.length });
        if (matches) {
          doc.replaceSelection(line.replace(/^> /, ''));
          doc.setCursor({
            line: cursor.line,
            ch: cursor.ch - 2,
          });
        } else {
          doc.replaceSelection(`> ${line}`);
          doc.setCursor({
            line: cursor.line,
            ch: cursor.ch + 2,
          });
        }
      },
      link: () => {
        // this.handleOpenAddLink();
        let selected = doc.getSelection();

        if (selected.length === 0) {
          doc.replaceSelection('[텍스트](https://주소)');
          doc.setSelection(
            {
              line: cursor.line,
              ch: cursor.ch + 2,
            },
            {
              line: cursor.line,
              ch: cursor.ch + 5,
            },
          );
          return;
        }

        if (selected === '텍스트') {
          const selectLeftAndRight = doc.getRange(
            {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            },
            {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            },
          );
          if (/\[(.*)\]\(https:\/\/주소\)/.test(selectLeftAndRight)) {
            selected = selectLeftAndRight;
            doc.setSelection(
              {
                line: selection.start.line,
                ch: selection.start.ch - 2,
              },
              {
                line: selection.end.line,
                ch: selection.end.ch + 2,
              },
            );
            selection.start = {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            };
            selection.end = {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            };
          }
        }

        if (/\[(.*)\)/.test(selected)) {
          const plain = selected
            .replace(/^\[/, '') // remove starting ~~
            .replace(/\)$/, ''); // remove ending ~~
          doc.replaceSelection(plain);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 4 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`[${selected}](https://주소)`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 4 },
          );
        }
      },
      image: () => {
        this.props.onUploadClick();
      },
      codeblock: () => {
        const selected = doc.getSelection();
        if (selected.length === 0) {
          doc.replaceSelection('```\n소스코드를 입력하세요\n```');
          doc.setSelection(
            {
              line: cursor.line + 1,
              ch: 0,
            },
            {
              line: cursor.line + 1,
              ch: 9,
            },
          );
          return;
        }
        doc.replaceSelection(`\`\`\`${selected}\`\`\``);
      },
      youtube: () => {
        let selected = doc.getSelection();

        if (selected.length === 0) {
          doc.replaceSelection('!youtube[코드]');
          doc.setSelection(
            {
              line: cursor.line,
              ch: cursor.ch + 2,
            },
            {
              line: cursor.line,
              ch: cursor.ch + 5,
            },
          );
          return;
        }

        if (selected === '코드') {
          const selectLeftAndRight = doc.getRange(
            {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            },
            {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            },
          );
          if (/!youtube\[(.*)\[코드]/.test(selectLeftAndRight)) {
            selected = selectLeftAndRight;
            doc.setSelection(
              {
                line: selection.start.line,
                ch: selection.start.ch - 2,
              },
              {
                line: selection.end.line,
                ch: selection.end.ch + 2,
              },
            );
            selection.start = {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            };
            selection.end = {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            };
          }
        }

        if (/!youtube\[(.*)]/.test(selected)) {
          const plain = selected.replace(/^!youtube\[/, '').replace(/]$/, '');
          doc.replaceSelection(plain);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 4 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`!youtube[${selected}]`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 4 },
          );
        }
      },
      twitter: () => {
        let selected = doc.getSelection();

        if (selected.length === 0) {
          doc.replaceSelection('!twitter[코드]');
          doc.setSelection(
            {
              line: cursor.line,
              ch: cursor.ch + 2,
            },
            {
              line: cursor.line,
              ch: cursor.ch + 5,
            },
          );
          return;
        }

        if (selected === '코드') {
          const selectLeftAndRight = doc.getRange(
            {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            },
            {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            },
          );
          if (/!twitter\[(.*)\[코드]/.test(selectLeftAndRight)) {
            selected = selectLeftAndRight;
            doc.setSelection(
              {
                line: selection.start.line,
                ch: selection.start.ch - 2,
              },
              {
                line: selection.end.line,
                ch: selection.end.ch + 2,
              },
            );
            selection.start = {
              line: selection.start.line,
              ch: selection.start.ch - 2,
            };
            selection.end = {
              line: selection.end.line,
              ch: selection.end.ch + 2,
            };
          }
        }

        if (/!twitter\[(.*)]/.test(selected)) {
          const plain = selected.replace(/^!twitter\[/, '').replace(/]$/, '');
          doc.replaceSelection(plain);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch - 4 },
          );
          return;
        }
        if (selected.length > 0) {
          doc.replaceSelection(`!twitter[${selected}]`);
          doc.setSelection(
            {
              line: selection.start.line,
              ch: selection.start.ch,
            },
            { line: selection.end.line, ch: selection.end.ch + 4 },
          );
        }
      },
    };

    const handler = handlers[mode];
    if (!handler) return;

    handler();
    codemirror.focus();
  };

  handleAskConvert = () => {
    this.setState({
      askChangeEditor: true,
    });
  };

  handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { shadow } = this.state;
    const nextShadow = e.currentTarget.scrollTop > this.toolbarTop;
    if (shadow !== nextShadow) {
      this.setState({
        shadow: nextShadow,
      });
    }
    if (this.block.current) {
      const { clientWidth } = this.block.current;
      if (clientWidth !== this.state.clientWidth) {
        this.setState({
          clientWidth,
        });
      }
    }
  };

  render() {
    const { shadow, iOS } = this.state;
    return (
      <div className="CodeEditor material">
        <Toolbar
          shadow={shadow}
          mode="MARKDOWN"
          onClick={this.handleToolbarClick}
          onConvert={this.handleAskConvert}
          innerRef={this.toolbarElement}
        />
        {iOS ? (
          <textarea
            className="ios-fallback"
            value={this.props.body}
            onChange={this.onTextareaChange}
          />
        ) : (
          <div
            className="editor"
            ref={ref => {
              this.editor = ref;
            }}
          />
        )}
      </div>
    );
  }
}

export default CodeEditor;



// WEBPACK FOOTER //
// ./src/components/write/CodeEditor/CodeEditor.js